/**
 * Marketing — Pilot Targets (admin, read-only).
 *
 * A STATIC, client-side table of the AccelerOnc-lookalike pilot-target firms
 * from our outreach research (see frontend/src/data/pilotTargets.ts). There is
 * no backend call — the data is baked into the bundle — so this page is purely
 * a filter/sort/search view over PILOT_TARGETS.
 *
 * By design it carries ZERO contact PII: only org-level fields ship (firm
 * name, archetype, tier, calibration fit, health flag, website, pain hook).
 */
import React, { useMemo, useState } from 'react';
import {
  Search,
  Flame,
  Sun,
  Snowflake,
  Ban,
  HeartPulse,
  ExternalLink,
  ArrowUpDown,
  Target,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import {
  PILOT_TARGETS,
  PILOT_TARGET_COUNT,
  type PilotTarget,
  type PilotFit,
} from '../data/pilotTargets';

// --- Display metadata --------------------------------------------------------

const FIT_META: Record<
  Exclude<PilotFit, ''>,
  { label: string; badge: string; icon: React.ComponentType<{ className?: string }> }
> = {
  HOT: { label: 'Hot', badge: 'bg-red-100 text-red-700 border border-red-200', icon: Flame },
  WARM: { label: 'Warm', badge: 'bg-amber-100 text-amber-800 border border-amber-200', icon: Sun },
  COOL: { label: 'Cool', badge: 'bg-slate-100 text-slate-600 border border-slate-200', icon: Snowflake },
  DQ: { label: 'Disqualified', badge: 'bg-gray-100 text-gray-400 border border-gray-200 line-through', icon: Ban },
};

// Sort priority so HOT lands first, then WARM, COOL, DQ, and finally unrated.
const FIT_RANK: Record<PilotFit, number> = {
  HOT: 0,
  WARM: 1,
  COOL: 2,
  DQ: 3,
  '': 4,
};

type SortKey = 'fit' | 'org' | 'tier';
type SortDir = 'asc' | 'desc';

// Distinct archetypes, ordered by frequency, for the filter dropdown.
const ARCHETYPE_OPTIONS: string[] = (() => {
  const counts = new Map<string, number>();
  for (const t of PILOT_TARGETS) {
    if (t.archetype) counts.set(t.archetype, (counts.get(t.archetype) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
})();

const TIER_OPTIONS: number[] = Array.from(
  new Set(PILOT_TARGETS.map((t) => t.tier)),
).sort((a, b) => a - b);

const FitBadge: React.FC<{ fit: PilotFit }> = ({ fit }) => {
  if (!fit) {
    return <span className="text-xs text-apple-subtext">—</span>;
  }
  const meta = FIT_META[fit];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const MarketingTargetsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [archetypeFilter, setArchetypeFilter] = useState<string>('');
  const [fitFilter, setFitFilter] = useState<string>('');
  const [healthOnly, setHealthOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('fit');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Summary counts over the FULL set (filters don't change the header).
  const summary = useMemo(() => {
    const byFit: Record<PilotFit, number> = { HOT: 0, WARM: 0, COOL: 0, DQ: 0, '': 0 };
    const byTier = new Map<number, number>();
    for (const t of PILOT_TARGETS) {
      byFit[t.fit] += 1;
      byTier.set(t.tier, (byTier.get(t.tier) ?? 0) + 1);
    }
    return { byFit, byTier };
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = PILOT_TARGETS.filter((t) => {
      if (q && !t.org.toLowerCase().includes(q)) return false;
      if (tierFilter !== '' && t.tier !== Number(tierFilter)) return false;
      if (archetypeFilter && t.archetype !== archetypeFilter) return false;
      if (fitFilter && t.fit !== fitFilter) return false;
      if (healthOnly && !t.health) return false;
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'org') {
        cmp = a.org.localeCompare(b.org);
      } else if (sortKey === 'tier') {
        cmp = a.tier - b.tier || FIT_RANK[a.fit] - FIT_RANK[b.fit];
      } else {
        // fit: HOT-first, then tier, then name — the campaign's default view.
        cmp =
          FIT_RANK[a.fit] - FIT_RANK[b.fit] ||
          a.tier - b.tier ||
          a.org.localeCompare(b.org);
      }
      return cmp * dir;
    });
    return list;
  }, [search, tierFilter, archetypeFilter, fitFilter, healthOnly, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortHeader: React.FC<{ label: string; sk: SortKey; className?: string }> = ({
    label,
    sk,
    className,
  }) => (
    <th className={`px-3 py-2 text-left font-semibold text-apple-subtext ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => toggleSort(sk)}
        className="inline-flex items-center gap-1 hover:text-apple-ink"
      >
        {label}
        <ArrowUpDown
          className={`w-3 h-3 ${sortKey === sk ? 'text-apple-blue' : 'text-apple-divider'}`}
        />
      </button>
    </th>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-apple-ink flex items-center gap-2">
              <Target className="w-6 h-6 text-apple-blue" />
              Marketing — Pilot Targets
            </h1>
            <p className="text-sm text-apple-subtext mt-1">
              Researched AccelerOnc-lookalike firms for the pilot outreach campaign — static
              snapshot, no contact PII.
            </p>
          </div>
          <span className="inline-flex items-center px-2.5 py-1 bg-apple-bg border border-apple-divider rounded-full text-xs font-medium text-apple-ink">
            {PILOT_TARGET_COUNT} targets
          </span>
        </div>

        {/* Summary cards — fit + tier counts over the full set */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="rounded-xl border border-apple-divider bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-apple-subtext">Total</div>
            <div className="text-2xl font-semibold text-apple-ink mt-1">{PILOT_TARGET_COUNT}</div>
          </div>
          {(['HOT', 'WARM', 'COOL'] as const).map((f) => {
            const meta = FIT_META[f];
            const Icon = meta.icon;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFitFilter((cur) => (cur === f ? '' : f))}
                className={`rounded-xl border p-4 text-left transition-all hover:shadow-card-hover ${
                  fitFilter === f ? 'border-apple-blue ring-1 ring-apple-blue' : 'border-apple-divider'
                } bg-white`}
                title={`Filter to ${meta.label} targets`}
              >
                <div className="text-xs uppercase tracking-wide text-apple-subtext flex items-center gap-1">
                  <Icon className="w-3.5 h-3.5" />
                  {meta.label}
                </div>
                <div className="text-2xl font-semibold text-apple-ink mt-1">
                  {summary.byFit[f]}
                </div>
              </button>
            );
          })}
          <div className="rounded-xl border border-apple-divider bg-white p-4 col-span-2">
            <div className="text-xs uppercase tracking-wide text-apple-subtext">By tier</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
              {TIER_OPTIONS.map((tier) => (
                <button
                  key={tier}
                  type="button"
                  onClick={() =>
                    setTierFilter((cur) => (cur === String(tier) ? '' : String(tier)))
                  }
                  className={`text-sm tabular-nums ${
                    tierFilter === String(tier)
                      ? 'text-apple-blue font-semibold'
                      : 'text-apple-ink hover:text-apple-blue'
                  }`}
                  title={`Filter to tier ${tier}`}
                >
                  T{tier}:{' '}
                  <span className="font-semibold">{summary.byTier.get(tier) ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="sticky top-16 z-10 bg-apple-bg/80 backdrop-blur border border-apple-divider rounded-xl p-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-apple-subtext" />
              </div>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by org name..."
                className="block w-full pl-9 pr-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-apple-ink whitespace-nowrap select-none cursor-pointer">
              <input
                type="checkbox"
                checked={healthOnly}
                onChange={(e) => setHealthOnly(e.target.checked)}
                className="rounded border-apple-divider text-apple-blue focus:ring-apple-blue"
              />
              <HeartPulse className="w-4 h-4 text-rose-500" />
              Health only
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All tiers</option>
              {TIER_OPTIONS.map((tier) => (
                <option key={tier} value={tier}>
                  Tier {tier}
                </option>
              ))}
            </select>

            <select
              value={archetypeFilter}
              onChange={(e) => setArchetypeFilter(e.target.value)}
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All archetypes</option>
              {ARCHETYPE_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>

            <select
              value={fitFilter}
              onChange={(e) => setFitFilter(e.target.value)}
              className="px-3 py-2 border border-apple-divider rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-apple-blue"
            >
              <option value="">All fit</option>
              <option value="HOT">Hot</option>
              <option value="WARM">Warm</option>
              <option value="COOL">Cool</option>
              <option value="DQ">Disqualified</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-apple-divider rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-apple-bg border-b border-apple-divider">
                <tr>
                  <SortHeader label="Org" sk="org" />
                  <th className="px-3 py-2 text-left font-semibold text-apple-subtext">
                    Archetype
                  </th>
                  <SortHeader label="Tier" sk="tier" className="text-center" />
                  <SortHeader label="Fit" sk="fit" />
                  <th
                    className="px-3 py-2 text-center font-semibold text-apple-subtext"
                    title="Health vertical"
                  >
                    <HeartPulse className="w-4 h-4 inline text-rose-500" />
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-apple-subtext">
                    Pain hook
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-apple-subtext">Website</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-apple-divider">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-apple-subtext">
                      No targets match the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((t: PilotTarget, i: number) => (
                    <tr
                      key={`${t.org}-${t.tier}-${i}`}
                      className={`hover:bg-apple-bg/60 ${t.fit === 'DQ' ? 'opacity-60' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-apple-ink whitespace-nowrap max-w-xs truncate">
                        {t.org}
                      </td>
                      <td className="px-3 py-2 text-apple-subtext whitespace-nowrap">
                        {t.archetype || '—'}
                      </td>
                      <td className="px-3 py-2 text-center text-apple-ink tabular-nums">
                        {t.tier}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <FitBadge fit={t.fit} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {t.health ? (
                          <HeartPulse
                            className="w-4 h-4 inline text-rose-500"
                            aria-label="Health vertical"
                          />
                        ) : (
                          <span className="text-apple-divider">—</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-apple-subtext max-w-md truncate"
                        title={t.painHook || undefined}
                      >
                        {t.painHook || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {t.website ? (
                          <a
                            href={t.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-apple-blue hover:underline"
                          >
                            Visit
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-apple-divider">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-xs text-apple-subtext border-t border-apple-divider bg-apple-bg/50">
            Showing {rows.length} of {PILOT_TARGET_COUNT} targets
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MarketingTargetsPage;
